/**
 * CartCalculations.js
 * Handles all price calculations, formatting, and total computations for the cart
 */

export class CartCalculations {
  /**
   * Format item price with currency
   * @param {Object} item - Cart item
   * @param {string} currency - Currency code (default: 'PLN')
   * @returns {string} Formatted price string
   */
  static formatItemPrice(item, currency = 'PLN') {
    const totalPrice = item.totalPrice || (item.price * item.quantity);
    const validPrice = parseFloat(totalPrice) || 0;
    return `${validPrice.toFixed(2)} ${currency}`;
  }

  /**
   * Calculate total for cart items
   * @param {Array} cartItems - Array of cart items
   * @returns {number} Total price
   */
  static calculateCartTotal(cartItems) {
    return cartItems.reduce((sum, item) => sum + (item.totalPrice || item.price * item.quantity), 0);
  }

  /**
   * Get display total - uses backend order total if available, otherwise calculates from items
   * @param {string} orderState - Current order state
   * @param {Object} pendingOrder - Pending order data (if applicable)
   * @param {Array} cartItems - Current cart items
   * @returns {number} Display total
   */
  static getDisplayTotal(orderState, pendingOrder, cartItems) {
    if ((orderState === 'pending-payment' || orderState === 'completed') && pendingOrder && pendingOrder.total != null) {
      const backendTotal = Number(pendingOrder.total);
      if (!Number.isNaN(backendTotal)) {
        return backendTotal;
      }
    }
    return this.calculateCartTotal(cartItems);
  }

  /**
   * Calculate totals for cart confirmation
   * @param {Array} cartItems - Array of cart items
   * @returns {Object} Object with subtotal and total
   */
  static calculateTotals(cartItems) {
    const total = this.calculateCartTotal(cartItems);
    return {
      subtotal: total,
      total: total
    };
  }

  /**
   * Get currency from order or items
   * @param {Object} pendingOrder - Pending order data
   * @param {Array} cartItems - Cart items
   * @returns {string} Currency code
   */
  static getCurrency(pendingOrder, cartItems) {
    return pendingOrder?.currency || cartItems[0]?.currency || 'PLN';
  }

  /**
   * Count total items in cart
   * @param {Array} cartItems - Array of cart items
   * @returns {number} Total item count
   */
  static getTotalItemCount(cartItems) {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  }
}
